/**
 * RulesEditor (MUI)
 *
 * 테넌트별 .claude/rules/ 파일 편집기 — 개별 테넌트 규칙만 관리.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";

import * as tenantsApi from "../api/tenants";
import type { RuleFile } from "../api/types";

const MAX_RULE_LEN = 10_000;

interface RulesEditorProps {
  tenantCode: string;
}

interface ToastState {
  message: string;
  severity: "success" | "error" | "info";
}

export function RulesEditor({ tenantCode }: RulesEditorProps) {
  const [rules, setRules] = useState<RuleFile[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [toast, setToast] = useState<ToastState | null>(null);

  const dirty = content !== original;

  const loadRules = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await tenantsApi.fetchClaudeRules(tenantCode);
      setRules(data);
      setSelected((prev) => {
        if (prev) return prev;
        return data.length > 0 ? data[0].name : null;
      });
    } catch {
      setRules([]);
    } finally {
      setListLoading(false);
    }
  }, [tenantCode]);

  useEffect(() => {
    setSelected(null);
    setRules([]);
    setContent("");
    setOriginal("");
    loadRules();
  }, [tenantCode, loadRules]);

  useEffect(() => {
    if (!selected) return;
    setContentLoading(true);
    tenantsApi
      .fetchClaudeRule(tenantCode, selected)
      .then((data) => {
        setContent(data.content);
        setOriginal(data.content);
      })
      .catch(() => {
        setContent("");
        setOriginal("");
      })
      .finally(() => setContentLoading(false));
  }, [tenantCode, selected]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await tenantsApi.saveClaudeRule(tenantCode, selected, content);
      setOriginal(content);
      setToast({ message: `${selected} 저장 완료`, severity: "success" });
    } catch (e) {
      setToast({
        message: e instanceof Error ? e.message : "저장 실패",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    let name = trimmed;
    if (!name.endsWith(".md")) name = `${name}.md`;
    const template = `# ${name.replace(".md", "")}\n\n`;
    try {
      await tenantsApi.saveClaudeRule(tenantCode, name, template);
      setNewName("");
      await loadRules();
      setSelected(name);
      setToast({ message: `${name} 생성 완료`, severity: "success" });
    } catch (e) {
      setToast({
        message: e instanceof Error ? e.message : "생성 실패",
        severity: "error",
      });
    }
  };

  const handleDelete = async (name: string) => {
    if (!window.confirm(`${name} 을(를) 삭제하시겠습니까?`)) return;
    try {
      await tenantsApi.deleteClaudeRule(tenantCode, name);
      if (selected === name) {
        setSelected(null);
        setContent("");
        setOriginal("");
      }
      await loadRules();
      setToast({ message: `${name} 삭제 완료`, severity: "success" });
    } catch (e) {
      setToast({
        message: e instanceof Error ? e.message : "삭제 실패",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100%", minHeight: 500, gap: 1 }}>
      {/* 좌측: 규칙 리스트 */}
      <Paper
        variant="outlined"
        sx={{ width: 300, display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            p: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <TextField
            size="small"
            placeholder="새 규칙 이름"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            fullWidth
          />
          <IconButton
            size="small"
            onClick={handleCreate}
            disabled={!newName.trim()}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {listLoading && rules.length === 0 ? (
            <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={16} />
            </Box>
          ) : rules.length === 0 ? (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
              <Typography variant="caption" color="text.secondary">
                규칙이 없습니다
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {rules.map((r) => (
                <ListItemButton
                  key={r.name}
                  selected={selected === r.name}
                  onClick={() => setSelected(r.name)}
                  sx={{ pr: 5 }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.name}
                        </Typography>
                      </Stack>
                    }
                  />
                  <Tooltip title="삭제">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(r.name);
                      }}
                      sx={{ position: "absolute", right: 4 }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {/* 우측: 에디터 */}
      <Paper
        variant="outlined"
        sx={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        {!selected ? (
          <Box sx={{ p: 3, flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="caption" color="text.secondary">
              규칙 파일을 선택하거나 새로 생성하세요.
            </Typography>
          </Box>
        ) : contentLoading ? (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                p: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2">{selected}</Typography>
                <Typography variant="caption" color="text.secondary">
                  .claude/rules/{selected}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {content.length.toLocaleString()} / {MAX_RULE_LEN.toLocaleString()}
              </Typography>
              {dirty && (
                <Button
                  size="small"
                  startIcon={<UndoIcon />}
                  onClick={() => setContent(original)}
                  disabled={saving}
                >
                  되돌리기
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                {saving ? "저장 중..." : "저장"}
              </Button>
            </Box>

            {toast && (
              <Alert
                severity={toast.severity}
                onClose={() => setToast(null)}
                sx={{ m: 1 }}
              >
                {toast.message}
              </Alert>
            )}

            <Box sx={{ flex: 1, p: 1 }}>
              <TextField
                multiline
                fullWidth
                value={content}
                onChange={(e) => setContent(e.target.value)}
                InputProps={{
                  sx: {
                    fontFamily: "monospace",
                    fontSize: 13,
                    alignItems: "flex-start",
                    height: "100%",
                  },
                }}
                sx={{
                  height: "100%",
                  "& .MuiInputBase-root": { height: "100%" },
                  "& .MuiInputBase-input": {
                    height: "100% !important",
                    overflow: "auto !important",
                  },
                }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
